import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Download, Upload, ShieldCheck, AlertTriangle, Loader2, ArrowLeft, Database } from "lucide-react";
import { toast } from "sonner";

export default function BackupAdmin() {
  const navigate = useNavigate();
  const [baixando, setBaixando] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [conteudo, setConteudo] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [restaurando, setRestaurando] = useState(false);
  const inputRef = useRef(null);

  const baixarBackup = async () => {
    setBaixando(true);
    try {
      const res = await api.get("/admin/backup/download", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const nome = m ? m[1] : `west-backup-${Date.now()}.json`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Backup baixado: ${nome}`);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setBaixando(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    setArquivo(null);
    setConteudo(null);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!parsed || !parsed.collections) {
          toast.error("Arquivo inválido: não parece um backup do West.");
          return;
        }
        setArquivo(f);
        setConteudo(parsed);
      } catch {
        toast.error("Não consegui ler o arquivo (JSON inválido).");
      }
    };
    reader.readAsText(f);
  };

  const restaurar = async () => {
    if (!conteudo) return;
    setRestaurando(true);
    try {
      const res = await api.post("/admin/restore-upload", { collections: conteudo.collections, confirmar: "RESTAURAR" });
      toast.success("Backup restaurado com sucesso!");
      setConfirmando(false);
      setArquivo(null);
      setConteudo(null);
      if (inputRef.current) inputRef.current.value = "";
      setTimeout(() => window.location.reload(), 800);
      return res;
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setRestaurando(false);
    }
  };

  const counts = conteudo?.counts || {};
  const labels = {
    fornecedores: "Fornecedores",
    combinacoes: "Tecidos",
    rolos: "Rolos",
    movimentacoes: "Histórico",
    romaneios: "Romaneios",
    destinos: "Destinos",
  };

  return (
    <div data-testid="backup-admin-page" className="mx-auto max-w-3xl">
      <button
        onClick={() => navigate(-1)}
        data-testid="backup-voltar"
        className="mb-3 flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={16} /> Voltar
      </button>

      <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
        <Database size={28} /> Backup e restauração
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Seu seguro. Baixe um backup antes de qualquer mudança importante; se algo der errado, restaure e volte ao
        estado anterior.
      </p>

      {/* Baixar */}
      <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Download size={20} /> Baixar backup
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Gera um arquivo <code>.json</code> com TODOS os seus dados (fornecedores, tecidos, rolos, romaneios,
          devoluções, histórico e destinos). O nome traz a data e hora. Guarde no seu PC ou Google Drive.
        </p>
        <button
          onClick={baixarBackup}
          disabled={baixando}
          data-testid="backup-download-btn"
          className="mt-4 flex items-center gap-2 rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {baixando ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
          Baixar backup agora
        </button>
      </section>

      {/* Restaurar */}
      <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Upload size={20} /> Restaurar backup
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Suba um arquivo de backup que você baixou antes. O sistema vai voltar ao estado de quando aquele backup foi
          feito.
        </p>

        <input
          ref={inputRef}
          type="file"
          accept="application/json,.json"
          onChange={onPickFile}
          data-testid="backup-file-input"
          className="mt-4 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
        />

        {conteudo && (
          <div className="mt-4 rounded-md bg-slate-50 p-4" data-testid="backup-preview">
            <div className="text-sm font-semibold text-slate-700">Arquivo selecionado: {arquivo?.name}</div>
            <div className="mt-1 text-xs text-slate-500">
              Feito em: {conteudo.exportado_em ? new Date(conteudo.exportado_em).toLocaleString("pt-BR") : "—"}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
              {Object.entries(labels).map(([k, lbl]) => (
                <span key={k}>
                  <strong>{counts[k] ?? 0}</strong> {lbl}
                </span>
              ))}
            </div>

            <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" />
              <span>
                <strong>Atenção:</strong> restaurar vai <strong>SUBSTITUIR os dados atuais</strong> pelos dados deste
                backup. (Por segurança, o sistema salva automaticamente o estado atual antes de sobrescrever.)
              </span>
            </div>

            {!confirmando ? (
              <button
                onClick={() => setConfirmando(true)}
                data-testid="backup-restore-btn"
                className="mt-3 flex items-center gap-2 rounded-md border-2 border-red-300 bg-white px-5 py-3 text-sm font-semibold text-red-700 hover:bg-red-50"
              >
                <Upload size={18} /> Restaurar este backup
              </button>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-700">Tem certeza?</span>
                <button
                  onClick={restaurar}
                  disabled={restaurando}
                  data-testid="backup-restore-confirm"
                  className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {restaurando ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
                  Sim, restaurar e substituir
                </button>
                <button
                  onClick={() => setConfirmando(false)}
                  disabled={restaurando}
                  data-testid="backup-restore-cancel"
                  className="rounded-md border-2 border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
